import React, {Fragment, useEffect, useState, useContext} from 'react';
import {MasterSchemaView} from '../master-schema-view';
import {Tabs, Row, Col, Select, Dropdown, Button, Popconfirm, Space, Typography, Form, Input} from 'antd';
import {map, isUndefined, clone} from 'lodash';
import HttpCodes from 'http-codes';
import {PlusCircleOutlined, DownOutlined, DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {OpenApiContext} from '../../openapi-context';
import {MarkdownView} from '../markdown-block';

const computeResponseTitleStyle = (code) => {
    if (code < 300 || code === 'default') {
        return 'successResponse';
    } else if (code < 500) {
        return 'clientErrorResponse';
    } else {
        return 'serverErrorResponse';
    }
};

function NewResponseForm({onChange}) {
    const [form] = Form.useForm();
    const mimeTypes = ['application/json', 'application/xml', 'plain/text', 'application/www-form-urlencoded', 'multipart/form-data'];

    const handleAdd = async () => {
        try {
            const values = await form.validateFields();
            onChange({type: 'add-response-code', ...values});
            form.resetFields();
        } catch (err) {
            console.error(err);
        }
    };
    return (
        <Fragment>
            <Typography.Paragraph>Select the code and the schema type and click the Add button to add a new response to this operation</Typography.Paragraph>
            <Form form={form} onFinish={handleAdd}>
                <Form.Item label="Status Code" name="code" rules={[{required: true, message: 'Status Code is required'}]}>
                    <Select style={{width: '100%'}}>
                        {map(HttpCodes, (val, text) => (
                            <Select.Option key={val} value={val.toString()}>
                                {val} - {text}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label="MIME Type" name="contentType" rules={[{required: true, message: 'Type is required'}]}>
                    <Select style={{width: '100%'}} placeholder="No Mime type specified for this response">
                        {mimeTypes.map((type, idx) => (
                            <Select.Option key={idx} value={type}>
                                {type}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item>
                    <Button className="promoteButton" htmlType="submit" type="primary" block icon={<PlusOutlined />}>
                        Add Response
                    </Button>
                </Form.Item>
            </Form>
        </Fragment>
    );
}

function ResponseViewer({spec, code}) {
    const [currentType, setCurrentType] = useState();
    const schema = (content) => content && Object.values(content)[currentType || 0] && Object.values(content)[currentType || 0].schema;
    const {dispatch} = useContext(OpenApiContext);
    const [mimeTypes, setMimeTypes] = useState([]);

    useEffect(() => {
        if (spec) {
            const contentKeys = Object.keys(spec.content || {});
            if (contentKeys.length > 0) {
                setCurrentType(0);
            }
            setMimeTypes(contentKeys);
        }
    }, [spec]);

    function handleChangeCurrentType(typeIndex) {
        dispatch({type: 'setCurrentIndexResponse', index: typeIndex});
        setCurrentType(typeIndex);
    }

    return (
        <Space orientation="vertical" style={{width: '100%'}}>
            <h4 className={computeResponseTitleStyle(code)}>
                <MarkdownView source={spec.description} />
            </h4>
            {mimeTypes && (
                <Select style={{width: '100%'}} value={currentType} placeholder="No Mime type specified for this response" onChange={handleChangeCurrentType}>
                    {mimeTypes.map((type, idx) => (
                        <Select.Option key={idx} value={idx}>
                            {type}
                        </Select.Option>
                    ))}
                </Select>
            )}
            <MasterSchemaView schema={schema(spec.content)} type={schema(spec.content) && schema(spec.content).type} />
        </Space>
    );
}

function ResponseEditor({spec, code, editMode, onChange}) {
    const [currentType, setCurrentType] = useState();
    const [schema, setSchema] = useState();
    const {state, dispatch} = useContext(OpenApiContext);
    const [mimeTypes, setMimeTypes] = useState([]);

    useEffect(() => {
        if (mimeTypes) {
            if (!isUndefined(state.currentIndexResponse)) {
                setCurrentType(state.currentIndexResponse);
            } else if (Object.keys(mimeTypes).length > 0) {
                setCurrentType(0);
            }
        }
    }, [state.currentIndexResponse, mimeTypes]);

    useEffect(() => {
        if (spec && !isUndefined(currentType)) {
            if (spec.content && Object.keys(spec.content).length > 0 && Object.values(spec.content)[currentType || 0]) {
                setSchema(clone(Object.values(spec.content)[currentType || 0].schema));
            }
        }
    }, [currentType, spec]);

    useEffect(() => {
        if (spec && spec.content) {
            setMimeTypes(Object.keys(spec.content || {}));
        }
    }, [spec]);

    const menu = {
        onClick: addSchemaType,
        items: [
            {key: 'application/json', label: 'application/json'},
            {key: 'application/xml', label: 'application/xml'},
            {key: 'plain/text', label: 'plain/text'},
            {key: 'application/www-form-urlencoded', label: 'application/www-form-urlencoded'},
            {key: 'multipart/form-data', label: 'multipart/form-data'},
        ],
    };

    function addSchemaType(type) {
        if (!mimeTypes.includes(type.key)) {
            const action = {type: 'add-response-content-type', code, contentType: type.key, value: {schema: {type: 'object', properties: {}}}};
            dispatch({type: 'setCurrentIndexResponse', index: mimeTypes.length});
            setCurrentType(mimeTypes.length);
            onChange(action);
        } else {
            console.log('mime type already exists', type.key);
        }
    }

    function deleteSchemaType() {
        onChange({type: 'rm-response-content-type', code, contentType: mimeTypes[currentType]});
        if (mimeTypes.length > 0) {
            dispatch({type: 'setCurrentIndexResponse', index: 0});
        } else {
            dispatch({type: 'setCurrentIndexResponse', index: null});
        }
    }

    function handleChangeCurrentType(typeIndex) {
        dispatch({type: 'setCurrentIndexResponse', index: typeIndex});
        setCurrentType(typeIndex);
    }

    function handleSchemaChange(schemaChangeSet) {
        let action = null;

        schemaChangeSet.schemaPath = schemaChangeSet.schemaPath || makeContextPath();
        if (schemaChangeSet.name && !schemaChangeSet.op) {
            schemaChangeSet.op = 'move';
            action = {type: 'update-response-content-schema', changeSet: schemaChangeSet};
        } else {
            action = {type: 'update-response-content-schema', changeSet: schemaChangeSet};
        }
        onChange(action);
    }

    function handleChangeCode(newCode) {
        onChange({type: 'change-response-code', from: code, to: newCode});
    }

    const updateDescription = function (val) {
        const changes = {};
        changes.description = val;
        onChange({type: 'update-response-code', code, value: changes});
    };

    const makeContextPath = () => {
        if (spec && mimeTypes) {
            return `${code}.content.${mimeTypes[currentType]}.schema`;
        }
        return null;
    };

    return (
        <Space orientation="vertical" style={{width: '100%'}}>
            <Select style={{width: '100%'}} defaultValue={code} onChange={handleChangeCode}>
                {map(HttpCodes, (val, text) => (
                    <Select.Option key={val} value={val.toString()}>
                        {val} - {text}
                    </Select.Option>
                ))}
            </Select>
            <Input value={spec.description} onChange={(e) => updateDescription(e.target.value)} />
            {mimeTypes && (
                <Row>
                    <Col lg={18} sm={24} xs={24} id="selec-view">
                        <Select style={{width: '100%'}} value={currentType} placeholder="No Mime type specified for this response" onChange={handleChangeCurrentType}>
                            {mimeTypes.map((type, idx) => (
                                <Select.Option key={idx} value={idx}>
                                    {type}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={5} offset={1}>
                        <Space style={{width: '100%'}}>
                            <Dropdown menu={menu}>
                                <Button className="addButton" type="primary" icon={<PlusCircleOutlined />}>
                                    Add <DownOutlined />
                                </Button>
                            </Dropdown>
                            <Popconfirm okType="default" title="Remove this content type?" onConfirm={deleteSchemaType}>
                                <Button className="deleteButton" type="danger" icon={<DeleteOutlined />} />
                            </Popconfirm>
                        </Space>
                    </Col>
                </Row>
            )}
            <MasterSchemaView schema={schema} contextPath={makeContextPath()} editMode={editMode} onChange={handleSchemaChange} />
        </Space>
    );
}

// As with RequestBody above, the Responses panel header + collapse caret
// are owned by the parent <Collapse> in operation-block.js. We used to
// double-wrap this in a <ToggleSection title="Responses"> here, which
// rendered a second "Responses" heading inside the panel body. Flat now.
export function Responses({responses, editMode, onChange}) {
    return responses ? (
        <Tabs
            type="card"
            tabPlacement="left"
            size="small"
            animated={false}
            items={[
                ...map(responses, (spec, code) => ({
                    key: code,
                    label: <span className={computeResponseTitleStyle(code)}>{code}</span>,
                    children: editMode
                        ? <ResponseEditor spec={spec} code={code} editMode={editMode} onChange={onChange} />
                        : <ResponseViewer spec={spec} code={code} />,
                })),
                ...(editMode
                    ? [{
                        key: 'NEW',
                        label: <PlusOutlined />,
                        children: <NewResponseForm onChange={onChange} />,
                    }]
                    : []),
            ]}
        />
    ) : null;
}
